import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiEdit3,
  FiImage,
  FiInfo,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiSettings,
  FiX,
} from "react-icons/fi";
import { FaPalette } from "react-icons/fa";

import {
  getSettings,
  updateSettings,
  updateSettingsColors,
} from "../../../services/settingsServices";

import useTheme from "../../../theme/useTheme";
// import MediaPickerModal from "../Projets/components/MediaPickerModal";
import AdminMediaPickerDrawer from "./../_shared/ui/AdminMediaPickerDrawer";
import { toAbsolutePublicUrl } from "../../../services/mediaLibraryServices";

import SettingsDrawer from "./components/SettingsDrawer";
import IdentitySection from "./components/IdentitySection";
import ContactSocialSection from "./components/ContactSocialSection";
import HeroImagesSection from "./components/HeroImagesSection";
import AlertSection from "./components/AlertSection";
import AboutSection from "./components/AboutSection";
import ColorsSection from "./components/ColorsSection";
import SiteQrCodeSection from "./components/SiteQrCodeSection";

const HEX_RE = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const toDatetimeLocalValue = (iso) => {
  if (!iso) return "";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const INFO_FIELDS = [
  "nom_mairie",
  "adresse",
  "code_postal",
  "ville",
  "logo_mairie",
  "email_contact",
  "telephone_contact",
  "site_web",
  "link_facebook",
  "link_instagram",
  "link_linkedin",
  "link_youtube",
  "photoURL1",
  "photoURL2",
  "photoURL3",
  "titre_about",
  "description_about",
  "photoURL_about",
];

const DRAWERS = {
  identity: {
    title: "Identité de la commune",
    subtitle: "Nom, adresse, site officiel et logo de la mairie.",
  },
  contact: {
    title: "Contact et réseaux sociaux",
    subtitle: "Coordonnées affichées sur le site et liens vers vos réseaux.",
  },
  hero: {
    title: "Images d’accueil",
    subtitle: "Photos utilisées dans la grande bannière de la page d’accueil.",
  },
  alert: {
    title: "Message d’alerte",
    subtitle:
      "Bandeau temporaire affiché sur l’accueil : travaux, intempéries, information urgente…",
  },
  about: {
    title: "Section À propos",
    subtitle: "Texte de présentation de la commune affiché sur l’accueil.",
  },
  colors: {
    title: "Couleurs du site",
    subtitle: "Tonalités principales et couleur des boutons.",
  },
};

const initialFormState = {
  id: 1,
  nom_mairie: "",
  adresse: "",
  code_postal: "",
  ville: "",
  logo_mairie: "",
  email_contact: "",
  telephone_contact: "",
  site_web: "",
  link_facebook: "",
  link_instagram: "",
  link_linkedin: "",
  link_youtube: "",
  photoURL1: "",
  photoURL2: "",
  photoURL3: "",

  alert_is_active: false,
  alert_type: "info",
  alert_title: "",
  alert_message: "",
  alert_link_label: "",
  alert_link_url: "",
  alert_start_at: "",
  alert_end_at: "",

  titre_about: "",
  description_about: "",
  photoURL_about: "",

  primary_color: "#007AC9",
  primary_color2: "#FB923C",
  button_primary_color: "#007AC9",
  button_primary_color2: "#4BABE9",
};

export default function SettingsFormPage() {
  const [loading, setLoading] = useState(true);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingColors, setSavingColors] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [activeDrawer, setActiveDrawer] = useState(null);

  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTargetField, setMediaTargetField] = useState(null);

  const {
    applyPrimary,
    applySecondary,
    applyBtn1,
    applyBtn2,
    toggleDark,
    dark,
  } = useTheme();

  const [form, setForm] = useState(initialFormState);

  const heroImages = useMemo(
    () =>
      [form.photoURL1, form.photoURL2, form.photoURL3]
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean),
    [form.photoURL1, form.photoURL2, form.photoURL3]
  );

  const addressLine = useMemo(() => {
    const parts = [
      form.adresse,
      [form.code_postal, form.ville].filter(Boolean).join(" "),
    ].filter(Boolean);

    return parts.join(" — ");
  }, [form.adresse, form.code_postal, form.ville]);

  const completion = useMemo(() => {
    const checks = [
      !!form.nom_mairie,
      !!form.logo_mairie,
      !!form.adresse,
      !!form.email_contact,
      !!form.telephone_contact,
      heroImages.length > 0,
      !!form.titre_about,
      !!form.description_about,
    ];

    const done = checks.filter(Boolean).length;

    return {
      done,
      total: checks.length,
      percent: Math.round((done / checks.length) * 100),
    };
  }, [form, heroImages.length]);

  useEffect(() => {
    const fetchSettings = async () => {
      setError("");
      setLoading(true);

      try {
        const data = await getSettings();
        const settings = Array.isArray(data) ? data[0] : data;

        if (settings) {
          setForm((prev) => ({
            ...prev,
            ...settings,

            button_primary_color:
              settings.button_primary_color || prev.button_primary_color,
            button_primary_color2:
              settings.button_primary_color2 || prev.button_primary_color2,

            alert_start_at: toDatetimeLocalValue(settings.alert_start_at),
            alert_end_at: toDatetimeLocalValue(settings.alert_end_at),
          }));

          if (settings.primary_color && HEX_RE.test(settings.primary_color)) {
            applyPrimary(settings.primary_color);
          }

          if (settings.primary_color2 && HEX_RE.test(settings.primary_color2)) {
            applySecondary(settings.primary_color2);
          }

          if (
            settings.button_primary_color &&
            HEX_RE.test(settings.button_primary_color)
          ) {
            applyBtn1(settings.button_primary_color);
          }

          if (
            settings.button_primary_color2 &&
            HEX_RE.test(settings.button_primary_color2)
          ) {
            applyBtn2(settings.button_primary_color2);
          }
        }
      } catch (fetchError) {
        console.error("Erreur getSettings:", fetchError);
        setError("Impossible de charger les paramètres du site.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDrawer = (key) => {
    setError("");
    setSuccess("");
    setActiveDrawer(key);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  const onOpenLibrary = (fieldName) => {
    setMediaTargetField(fieldName);
    setMediaOpen(true);
  };

  const handleMediaSelect = (item) => {
    if (!item?.url || !mediaTargetField) return;

    const absUrl = toAbsolutePublicUrl(item.url);

    setForm((current) => ({
      ...current,
      [mediaTargetField]: absUrl,
    }));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeColor = (name) => (e) => {
    const value = e.target.value;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleApplyColors = () => {
    if (HEX_RE.test(form.primary_color)) applyPrimary(form.primary_color);
    if (HEX_RE.test(form.primary_color2)) applySecondary(form.primary_color2);
    if (HEX_RE.test(form.button_primary_color)) {
      applyBtn1(form.button_primary_color);
    }
    if (HEX_RE.test(form.button_primary_color2)) {
      applyBtn2(form.button_primary_color2);
    }
  };

  const handleSaveInfo = async ({ closeAfterSave = true } = {}) => {
    setSavingInfo(true);
    setError("");
    setSuccess("");

    try {
      const payload = {};

      for (const key of INFO_FIELDS) {
        payload[key] = form[key];
      }

      const saved = await updateSettings(payload);

      if (!saved) {
        throw new Error("updateSettings failed");
      }

      setSuccess("Informations enregistrées avec succès.");

      if (closeAfterSave) {
        closeDrawer();
      }
    } catch (saveError) {
      console.error("Erreur updateSettings:", saveError);
      setError("Erreur pendant l’enregistrement des informations.");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveColors = async () => {
    const fields = [
      "primary_color",
      "primary_color2",
      "button_primary_color",
      "button_primary_color2",
    ];

    for (const key of fields) {
      if (!HEX_RE.test(form[key] || "")) {
        setError(`Couleur invalide (${key}). Utilisez #RRGGBB ou #RGB.`);
        return;
      }
    }

    setSavingColors(true);
    setError("");
    setSuccess("");

    try {
      const res = await updateSettingsColors({
        primary_color: form.primary_color,
        primary_color2: form.primary_color2,
        button_primary_color: form.button_primary_color,
        button_primary_color2: form.button_primary_color2,
      });

      if (!res?.ok) {
        await updateSettings({
          primary_color: form.primary_color,
          primary_color2: form.primary_color2,
          button_primary_color: form.button_primary_color,
          button_primary_color2: form.button_primary_color2,
        });
      }

      handleApplyColors();
      setSuccess("Couleurs enregistrées avec succès.");
      closeDrawer();
    } catch (saveError) {
      console.error("Erreur updateSettingsColors:", saveError);
      setError("Erreur pendant l’enregistrement des tonalités.");
    } finally {
      setSavingColors(false);
    }
  };

  const renderDrawerContent = () => {
    if (activeDrawer === "identity") {
      return (
        <IdentitySection
          form={form}
          onChange={onChange}
          onOpenLibrary={onOpenLibrary}
        />
      );
    }

    if (activeDrawer === "contact") {
      return <ContactSocialSection form={form} onChange={onChange} />;
    }

    if (activeDrawer === "hero") {
      return (
        <HeroImagesSection
          form={form}
          onChange={onChange}
          onOpenLibrary={onOpenLibrary}
        />
      );
    }

    if (activeDrawer === "alert") {
      return (
        <AlertSection form={form} setForm={setForm} setError={setError} />
      );
    }

    if (activeDrawer === "about") {
      return (
        <AboutSection
          form={form}
          onChange={onChange}
          onOpenLibrary={onOpenLibrary}
        />
      );
    }

    if (activeDrawer === "colors") {
      return (
        <ColorsSection
          form={form}
          onChangeColor={onChangeColor}
          onApplyColors={handleApplyColors}
          onSaveColors={handleSaveColors}
          savingColors={savingColors}
          dark={dark}
          toggleDark={toggleDark}
        />
      );
    }

    return null;
  };

  const drawerFooter = (() => {
    if (!activeDrawer) return null;

    if (activeDrawer === "alert" || activeDrawer === "colors") {
      return (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={closeDrawer}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FiX />
            Fermer
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={closeDrawer}
          disabled={savingInfo}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FiX />
          Annuler
        </button>

        <button
          type="button"
          onClick={() => handleSaveInfo({ closeAfterSave: true })}
          disabled={savingInfo}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-60"
        >
          <FiSave />
          {savingInfo ? "Enregistrement..." : "Enregistrer"}
        </button>
      </div>
    );
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-950">
        <div className="mx-auto max-w-[1500px] space-y-4">
          <div className="h-40 animate-pulse rounded-[32px] bg-slate-200 dark:bg-slate-800" />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-56 animate-pulse rounded-[32px] bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const drawerMeta = activeDrawer ? DRAWERS[activeDrawer] : null;

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <header className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative p-5 md:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-600 via-cyan-400 to-emerald-400" />

            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-950/40 dark:text-sky-200">
                  <FiSettings />
                  Paramètres du site
                </div>

                <h1 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                  Informations générales
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Retrouvez ici les informations visibles sur le site de la
                  commune. Chaque partie se modifie séparément pour éviter les
                  erreurs.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-sky-600 text-lg font-bold text-white">
                    {completion.percent}%
                  </div>

                  <div>
                    <div className="text-sm font-bold">
                      Paramétrage complété
                    </div>

                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {completion.done} élément
                      {completion.done > 1 ? "s" : ""} renseigné
                      {completion.done > 1 ? "s" : ""} sur {completion.total}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {error ? (
          <Feedback tone="error" message={error} />
        ) : success ? (
          <Feedback tone="success" message={success} />
        ) : null}

        <section className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
          <div className="grid gap-4 md:grid-cols-2">
            <SummaryCard
              icon={<FiInfo />}
              title="Identité de la commune"
              description="Nom, adresse, logo et site web officiel."
              status={form.nom_mairie ? "Renseigné" : "À compléter"}
              onEdit={() => openDrawer("identity")}
            >
              <div className="flex items-center gap-4">
                <LogoPreview src={form.logo_mairie} />

                <div className="min-w-0">
                  <div className="truncate text-lg font-bold">
                    {form.nom_mairie || "Nom de la commune"}
                  </div>

                  <div className="mt-1 flex items-start gap-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    <FiMapPin className="mt-1 shrink-0" />
                    <span>
                      {addressLine || "Adresse non renseignée"}
                    </span>
                  </div>

                  {form.site_web ? (
                    <div className="mt-1 truncate text-xs text-sky-600 dark:text-sky-300">
                      {form.site_web}
                    </div>
                  ) : null}
                </div>
              </div>
            </SummaryCard>

            <SummaryCard
              icon={<FiMail />}
              title="Contact"
              description="Email, téléphone et réseaux sociaux."
              status={
                form.email_contact || form.telephone_contact
                  ? "Renseigné"
                  : "À compléter"
              }
              onEdit={() => openDrawer("contact")}
            >
              <div className="space-y-3 text-sm">
                <InfoLine
                  icon={<FiMail />}
                  label="Email"
                  value={form.email_contact || "Non renseigné"}
                />

                <InfoLine
                  icon={<FiPhone />}
                  label="Téléphone"
                  value={form.telephone_contact || "Non renseigné"}
                />

                <div className="flex flex-wrap gap-2 pt-1">
                  <SocialBadge label="Facebook" value={form.link_facebook} />
                  <SocialBadge label="Instagram" value={form.link_instagram} />
                  <SocialBadge label="LinkedIn" value={form.link_linkedin} />
                  <SocialBadge label="YouTube" value={form.link_youtube} />
                </div>
              </div>
            </SummaryCard>

            <SummaryCard
              icon={<FiImage />}
              title="Images d’accueil"
              description="Photos de la grande bannière d’accueil."
              status={`${heroImages.length}/3 image${
                heroImages.length > 1 ? "s" : ""
              }`}
              onEdit={() => openDrawer("hero")}
              className="md:col-span-2"
            >
              {heroImages.length ? (
                <div className="grid gap-3 md:grid-cols-3">
                  {heroImages.slice(0, 3).map((src, index) => (
                    <div
                      key={`${src}-${index}`}
                      className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 p-2 dark:border-slate-800 dark:bg-slate-950"
                    >
                      <div className="aspect-[16/7]">
                        <img
                          src={src}
                          alt={`Accueil ${index + 1}`}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyText text="Aucune image d’accueil renseignée." />
              )}
            </SummaryCard>

            <SummaryCard
              icon={<FiAlertTriangle />}
              title="Message d’alerte"
              description="Bandeau affiché sur la page d’accueil."
              status={form.alert_is_active ? "Actif" : "Inactif"}
              tone={form.alert_is_active ? "warning" : "slate"}
              onEdit={() => openDrawer("alert")}
            >
              <div
                className={`rounded-3xl border p-4 ${
                  form.alert_is_active
                    ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
                    : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                }`}
              >
                <div className="text-sm font-bold">
                  {form.alert_title || "Aucun titre"}
                </div>

                <div className="mt-1 line-clamp-3 text-sm leading-6">
                  {form.alert_message || "Aucun message d’alerte renseigné."}
                </div>
              </div>
            </SummaryCard>

            <SummaryCard
              icon={<FiInfo />}
              title="Section À propos"
              description="Présentation de la commune sur l’accueil."
              status={form.description_about ? "Renseigné" : "À compléter"}
              onEdit={() => openDrawer("about")}
            >
              <div className="flex gap-4">
                {form.photoURL_about ? (
                  <div className="h-24 w-32 shrink-0 overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 p-2 dark:border-slate-800 dark:bg-slate-950">
                    <img
                      src={form.photoURL_about}
                      alt={form.titre_about || "À propos"}
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                ) : null}

                <div className="min-w-0">
                  <div className="font-bold">
                    {form.titre_about || "Titre non renseigné"}
                  </div>

                  <p className="mt-2 line-clamp-4 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {form.description_about ||
                      "Texte de présentation non renseigné."}
                  </p>
                </div>
              </div>
            </SummaryCard>
          </div>

          <aside className="space-y-4">
            <SummaryCard
              icon={<FaPalette />}
              title="Couleurs du site"
              description="Identité visuelle et boutons principaux."
              status="Personnalisable"
              onEdit={() => openDrawer("colors")}
            >
              <div
                className="rounded-3xl p-5 text-white shadow-sm"
                style={{
                  background: `linear-gradient(135deg, ${form.primary_color}, ${form.primary_color2})`,
                }}
              >
                <div className="text-sm opacity-90">Aperçu</div>
                <div className="mt-1 text-2xl font-bold">
                  {form.nom_mairie || "Votre commune"}
                </div>

                <button
                  type="button"
                  className="mt-5 rounded-2xl px-4 py-2 text-sm font-bold text-white shadow"
                  style={{
                    background: `linear-gradient(90deg, ${form.button_primary_color}, ${form.button_primary_color2})`,
                  }}
                >
                  Bouton principal
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <ColorChip label="Primaire" value={form.primary_color} />
                <ColorChip label="Secondaire" value={form.primary_color2} />
                <ColorChip label="Bouton 1" value={form.button_primary_color} />
                <ColorChip label="Bouton 2" value={form.button_primary_color2} />
              </div>
            </SummaryCard>
            <SiteQrCodeSection />

            {/* <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="font-bold">Conseil</h3>

              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Pour un client de mairie, il vaut mieux modifier une seule
                partie à la fois. Cela limite les erreurs et rend la page plus
                facile à comprendre.
              </p>

              <div className="mt-4 rounded-3xl border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200">
                Les images peuvent être saisies avec une URL ou choisies dans
                la photothèque.
              </div>
            </div> */}
          </aside>
        </section>
      </div>

      <SettingsDrawer
        open={!!activeDrawer}
        onClose={closeDrawer}
        title={drawerMeta?.title || "Modifier"}
        subtitle={drawerMeta?.subtitle || ""}
        footer={drawerFooter}
        size={activeDrawer === "colors" ? "xl" : "lg"}
      >
        {renderDrawerContent()}
      </SettingsDrawer>

      <AdminMediaPickerDrawer
        open={mediaOpen}
        onClose={() => {
          setMediaOpen(false);
          setMediaTargetField(null);
        }}
        onSelect={(item) => {
          handleMediaSelect(item);
          setMediaOpen(false);
          setMediaTargetField(null);
        }}
      />
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  description,
  status,
  tone = "slate",
  onEdit,
  children,
  className = "",
}) {
  const toneClass =
    tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
      : "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300";

  return (
    <article
      className={`rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950">
            {icon}
          </div>

          <div>
            <h2 className="font-bold text-slate-950 dark:text-slate-50">
              {title}
            </h2>

            {description ? (
              <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FiEdit3 />
          Modifier
        </button>
      </div>

      <div className="mt-4">
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${toneClass}`}
        >
          <FiCheckCircle />
          {status}
        </span>
      </div>

      <div className="mt-5">{children}</div>
    </article>
  );
}

function Feedback({ tone = "success", message }) {
  const className =
    tone === "error"
      ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200"
      : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200";

  return (
    <div className={`rounded-3xl border px-4 py-3 text-sm font-medium ${className}`}>
      {message}
    </div>
  );
}

function LogoPreview({ src }) {
  return (
    <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950">
      {src ? (
        <img
          src={src}
          alt="Logo mairie"
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      ) : (
        <span className="text-xs text-slate-400">Logo</span>
      )}
    </div>
  );
}

function InfoLine({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1 text-slate-400">{icon}</span>
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {label}
        </div>
        <div className="break-words text-slate-700 dark:text-slate-200">
          {value}
        </div>
      </div>
    </div>
  );
}

function SocialBadge({ label, value }) {
  return (
    <span
      className={`rounded-full border px-3 py-1 text-xs font-bold ${
        value
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
          : "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-800 dark:bg-slate-950"
      }`}
    >
      {label}
    </span>
  );
}

function ColorChip({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-center gap-2">
        <span
          className="h-6 w-6 rounded-full border border-slate-200"
          style={{ backgroundColor: value }}
        />

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </div>
          <div className="text-xs font-bold text-slate-700 dark:text-slate-200">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyText({ text }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
      {text}
    </div>
  );
}